/**
 * Simulation Studio
 *
 * Workflow-level optimization via Replay mode.
 * 1. Workflow list with cost/latency/quality metrics
 * 2. Lumoz recommendations + interactive waterfall span tree
 * 3. Override editor panel (per-span, type-aware)
 * 4. Run simulation → aggregate results
 *
 * Currently driven by mock data. Backend wired in next phase.
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Zap, DollarSign, Clock, Sparkles, Play,
  ChevronDown, ChevronRight, Check, Pencil, Eye,
  RotateCcw, TrendingDown, TrendingUp, Minus,
  AlertTriangle, CheckCircle, X, FileText, Copy, ExternalLink
} from 'lucide-react';
import { palette, components as ds, withOpacity } from '../../utils/designTokens';

// ─────────────────────────────────────────────────────────────────────────────
// Design constants
// ─────────────────────────────────────────────────────────────────────────────
const SPAN_KIND_CONFIG = {
  LLM:       { color: palette.accents.primary.base,   bg: withOpacity(palette.accents.primary.base, 12), label: 'LLM',       editable: true  },
  RETRIEVER: { color: palette.status.success,          bg: withOpacity(palette.status.success, 10), label: 'RETRIEVER', editable: true  },
  TOOL:      { color: palette.accents.warm.base,       bg: withOpacity(palette.accents.warm.base, 10), label: 'TOOL',      editable: true  },
  CHAIN:     { color: palette.accents.secondary.base,  bg: withOpacity(palette.accents.secondary.base, 8), label: 'CHAIN',     editable: false },
  AGENT:     { color: palette.text.muted,              bg: palette.neutrals.surfaceSubtle, label: 'AGENT',     editable: false },
  EMBEDDING: { color: palette.status.info,             bg: withOpacity(palette.status.info, 8), label: 'EMBED',     editable: false },
};

const kindConfig = (kind) => SPAN_KIND_CONFIG[kind] || SPAN_KIND_CONFIG.AGENT;

const MOCK_MODELS = [
  { id: 'gpt-4o',               label: 'GPT-4o',                provider: 'openai',    cost_per_1m_in: 2.50,  cost_per_1m_out: 10.00 },
  { id: 'gpt-4o-mini',          label: 'GPT-4o mini',           provider: 'openai',    cost_per_1m_in: 0.15,  cost_per_1m_out: 0.60  },
  { id: 'gpt-4-turbo',          label: 'GPT-4 Turbo',           provider: 'openai',    cost_per_1m_in: 10.00, cost_per_1m_out: 30.00 },
  { id: 'gpt-3.5-turbo',        label: 'GPT-3.5 Turbo',         provider: 'openai',    cost_per_1m_in: 0.50,  cost_per_1m_out: 1.50  },
  { id: 'claude-3-5-sonnet',    label: 'Claude 3.5 Sonnet',     provider: 'anthropic', cost_per_1m_in: 3.00,  cost_per_1m_out: 15.00 },
  { id: 'claude-3-haiku',       label: 'Claude 3 Haiku',        provider: 'anthropic', cost_per_1m_in: 0.25,  cost_per_1m_out: 1.25  },
];

// Map business task names (from signal clusters) to workflow IDs.
// Keyed by the name the cluster actually carries, which Overview puts in the
// `workflow` param. These were the pre-rename intent labels, so the lookup
// silently missed and the studio opened with nothing selected.
const WORKFLOW_NAME_TO_ID = {
  'Search Documentation': 'wf-rag-001',
  'Answer Account Question': 'wf-support-001',
};

// ─────────────────────────────────────────────────────────────────────────────
// Mock data
// ─────────────────────────────────────────────────────────────────────────────
const MOCK_WORKFLOWS = [
  {
    id: 'wf-rag-001',
    name: 'RAG Pipeline',
    description: 'Retrieval-augmented generation with reranking',
    span_count: 8,
    llm_span_count: 2,
    avg_cost_usd: 0.0248,
    avg_latency_ms: 4820,
    avg_quality: 0.74,
    signal_count: 5,
    signals: ['hallucination', 'high_cost'],
    trace_count: 142,
  },
  {
    id: 'wf-support-001',
    name: 'Customer Support Agent',
    description: 'Multi-turn support with tool calls and escalation',
    span_count: 14,
    llm_span_count: 4,
    avg_cost_usd: 0.0124,
    avg_latency_ms: 3240,
    avg_quality: 0.82,
    signal_count: 2,
    signals: ['task_failure'],
    trace_count: 89,
  },
  {
    id: 'wf-trading-001',
    name: 'Trading Analysis',
    description: 'Multi-agent market analysis with tool integrations',
    span_count: 23,
    llm_span_count: 7,
    avg_cost_usd: 0.0431,
    avg_latency_ms: 11700,
    avg_quality: 0.69,
    signal_count: 8,
    signals: ['hallucination', 'high_cost', 'high_latency'],
    trace_count: 34,
  },
  {
    id: 'wf-code-001',
    name: 'Code Review Agent',
    description: 'Automated PR review with lint and security checks',
    span_count: 9,
    llm_span_count: 2,
    avg_cost_usd: 0.0082,
    avg_latency_ms: 2100,
    avg_quality: 0.88,
    signal_count: 1,
    signals: [],
    trace_count: 210,
  },
];

// Representative trace for RAG Pipeline
const MOCK_RAG_SPANS = [
  {
    span_id: 's1', parent_span_id: null,
    name: 'LangGraph Pipeline', span_kind: 'CHAIN',
    start_ms: 0,   duration_ms: 4820,
    prompt_tokens: 0, completion_tokens: 0, cost_usd: null,
    llm_model_name: null, llm_provider: null,
    quality: null,
  },
  {
    span_id: 's2', parent_span_id: 's1',
    name: 'retrieve_context', span_kind: 'RETRIEVER',
    start_ms: 10,  duration_ms: 450,
    prompt_tokens: 0, completion_tokens: 0, cost_usd: null,
    llm_model_name: null, llm_provider: null,
    quality: { context_relevance: 0.71 },
    retriever_config: { top_k: 10, similarity_threshold: 0.70, embedding_model: 'text-embedding-3-small' },
  },
  {
    span_id: 's3', parent_span_id: 's1',
    name: 'rerank_results', span_kind: 'TOOL',
    start_ms: 465, duration_ms: 120,
    prompt_tokens: 0, completion_tokens: 0, cost_usd: null,
    llm_model_name: null, llm_provider: null,
    quality: null,
    tool_schema: { name: 'rerank_results', description: 'Reranks retrieved documents by relevance', parameters: { type: 'object', properties: { documents: { type: 'array' }, query: { type: 'string' }, top_n: { type: 'integer' } } } },
  },
  {
    span_id: 's4', parent_span_id: 's1',
    name: 'generate_response', span_kind: 'CHAIN',
    start_ms: 590, duration_ms: 3900,
    prompt_tokens: 0, completion_tokens: 0, cost_usd: null,
    llm_model_name: null, llm_provider: null,
    quality: null,
  },
  {
    span_id: 's5', parent_span_id: 's4',
    name: 'format_prompt', span_kind: 'CHAIN',
    start_ms: 595, duration_ms: 80,
    prompt_tokens: 0, completion_tokens: 0, cost_usd: null,
    llm_model_name: null, llm_provider: null,
    quality: null,
  },
  {
    span_id: 's6', parent_span_id: 's4',
    name: 'ChatCompletion', span_kind: 'LLM',
    start_ms: 680, duration_ms: 3800,
    prompt_tokens: 2847, completion_tokens: 412, cost_usd: 0.0228,
    llm_model_name: 'gpt-4o', llm_provider: 'openai',
    quality: { answer_relevance: 0.74, faithfulness: 0.71 },
    system_prompt: 'You are a helpful assistant that answers questions accurately based on the provided context. Only use information from the context. If you cannot find the answer in the context, say so clearly.',
    temperature: 0.7,
    max_tokens: 1024,
  },
  {
    span_id: 's7', parent_span_id: 's1',
    name: 'validate_output', span_kind: 'CHAIN',
    start_ms: 4495, duration_ms: 315,
    prompt_tokens: 0, completion_tokens: 0, cost_usd: null,
    llm_model_name: null, llm_provider: null,
    quality: null,
  },
  {
    span_id: 's8', parent_span_id: 's7',
    name: 'faithfulness_check', span_kind: 'LLM',
    start_ms: 4500, duration_ms: 280,
    prompt_tokens: 180, completion_tokens: 42, cost_usd: 0.00045,
    llm_model_name: 'gpt-4o-mini', llm_provider: 'openai',
    quality: null,
    system_prompt: 'You are a faithfulness judge. Given a question, context, and answer, determine if the answer is faithful to the context. Respond with JSON: {"faithful": true/false, "reason": "..."}',
    temperature: 0.0,
    max_tokens: 256,
  },
];

const MOCK_RECOMMENDATIONS = {
  'wf-rag-001': [
    {
      id: 'rec-1',
      type: 'cost',
      action_label: 'gpt-4o → mini',
      tooltip: 'ChatCompletion accounts for 92% of cost. gpt-4o-mini has comparable answer_relevance (0.74 avg) at a fraction of the price.',
      span_id: 's6',
      span_name: 'ChatCompletion',
      impact: { cost: -68, latency: -22, quality: -3 },
      override: { model: 'gpt-4o-mini', provider: 'openai' },
      confidence: 'high',
      signal: 'high_cost',
    },
    {
      id: 'rec-2',
      type: 'latency',
      action_label: 'top_k 10 → 5',
      tooltip: 'Context relevance shows diminishing returns after top 5 documents. Reduces retrieval time with no quality impact.',
      span_id: 's2',
      span_name: 'retrieve_context',
      impact: { cost: 0, latency: -38, quality: 0 },
      override: { top_k: 5, similarity_threshold: 0.72 },
      confidence: 'high',
      signal: null,
    },
    {
      id: 'rec-3',
      type: 'quality',
      action_label: 'tighten system prompt',
      tooltip: '3 hallucination signals detected on ChatCompletion in the last 7 days. Explicit grounding instructions improve faithfulness.',
      span_id: 's6',
      span_name: 'ChatCompletion',
      impact: { cost: 0, latency: 0, quality: 14 },
      override: { system_prompt: 'You are a precise assistant that answers questions strictly based on the provided context. Do not add information not present in the context. If the context does not contain the answer, respond with: "I cannot find this information in the provided context." Always cite which part of the context supports your answer.' },
      confidence: 'medium',
      signal: 'hallucination',
    },
    {
      id: 'rec-4',
      type: 'cost',
      action_label: 'temp 0.7 → 0.2',
      tooltip: 'Lower temperature reduces token variance in RAG tasks, cutting output tokens and improving faithfulness consistency.',
      span_id: 's6',
      span_name: 'ChatCompletion',
      impact: { cost: -8, latency: -5, quality: 6 },
      override: { temperature: 0.2, max_tokens: 768 },
      confidence: 'medium',
      signal: null,
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function computeDepths(spans) {
  const parentMap = {};
  const depthMap = {};
  spans.forEach(s => { if (s.parent_span_id) parentMap[s.span_id] = s.parent_span_id; });
  function depth(id) {
    if (depthMap[id] !== undefined) return depthMap[id];
    const pid = parentMap[id];
    depthMap[id] = pid ? depth(pid) + 1 : 0;
    return depthMap[id];
  }
  spans.forEach(s => depth(s.span_id));
  return depthMap;
}

function fmtCost(usd) {
  if (usd === null || usd === undefined) return null;
  return usd < 0.01 ? `$${(usd * 1000).toFixed(2)}m` : `$${usd.toFixed(4)}`;
}

function fmtMs(ms) {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
}

function deltaColor(val) {
  if (val === 0 || val === null) return palette.text.muted;
  return val < 0 ? palette.status.success : palette.status.danger;
}

function deltaPrefix(val) {
  if (val === 0) return '';
  return val < 0 ? '' : '+';
}

// ─────────────────────────────────────────────────────────────────────────────
// WorkflowList — first view
// ─────────────────────────────────────────────────────────────────────────────
const SIGNAL_LABELS = {
  hallucination: { label: 'Groundedness',  color: palette.status.danger },
  high_cost:     { label: 'High Cost',     color: palette.accents.warm.base },
  high_latency:  { label: 'High Latency',  color: palette.status.warning },
  task_failure:  { label: 'Task Failure',  color: palette.status.danger },
};

const WorkflowList = ({ onSelect }) => (
  <div className="p-6 flex flex-col gap-4">
    <div className="mb-2">
      <h2 className="text-base font-semibold" style={{ color: palette.text.heading }}>Simulation Studio</h2>
      <p className="text-sm mt-1" style={{ color: palette.text.muted }}>
        Select a business task to optimize. Lumoz will analyse it and surface recommendations.
      </p>
    </div>

    <div className="grid grid-cols-1 gap-3" style={{ maxWidth: 800 }}>
      {MOCK_WORKFLOWS.map(wf => (
        <button
          key={wf.id}
          onClick={() => onSelect(wf)}
          className="text-left rounded-xl px-5 py-4 transition-all"
          style={{
            border: `1px solid ${palette.borders.medium}`,
            backgroundColor: 'transparent',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = palette.accents.primary.base; e.currentTarget.style.backgroundColor = withOpacity(palette.accents.primary.base, 4); }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = palette.borders.medium; e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1 flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold" style={{ color: palette.text.primary }}>{wf.name}</span>
                {wf.signal_count > 0 && (
                  <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: withOpacity(palette.status.danger, 12), color: palette.status.danger }}>
                    {wf.signal_count} signal{wf.signal_count !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <span className="text-xs" style={{ color: palette.text.muted }}>{wf.description}</span>
              {wf.signals.length > 0 && (
                <div className="flex gap-1.5 mt-1 flex-wrap">
                  {wf.signals.map(s => {
                    const cfg = SIGNAL_LABELS[s];
                    return cfg ? (
                      <span key={s} className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: withOpacity(cfg.color, 18), color: cfg.color, border: `1px solid ${withOpacity(cfg.color, 30)}` }}>
                        {cfg.label}
                      </span>
                    ) : null;
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center gap-6 shrink-0">
              <MetricPill icon={<DollarSign size={11} />} label="Avg Cost" value={`$${wf.avg_cost_usd.toFixed(4)}`} color={wf.avg_cost_usd > 0.03 ? palette.status.danger : palette.text.supporting} />
              <MetricPill icon={<Clock size={11} />} label="Avg Latency" value={fmtMs(wf.avg_latency_ms)} color={wf.avg_latency_ms > 8000 ? palette.status.warning : palette.text.supporting} />
              <MetricPill icon={<Sparkles size={11} />} label="Quality" value={(wf.avg_quality * 100).toFixed(0) + '%'} color={wf.avg_quality < 0.75 ? palette.accents.warm.base : palette.status.success} />
              <span className="text-xs" style={{ color: palette.text.muted }}>{wf.trace_count} traces</span>
              <div className="flex items-center gap-1 text-xs font-medium" style={{ color: palette.accents.primary.base }}>
                Simulate <ChevronRight size={13} />
              </div>
            </div>
          </div>
        </button>
      ))}
    </div>
  </div>
);

const MetricPill = ({ icon, label, value, color }) => (
  <div className="flex flex-col items-end gap-0.5">
    <span className="text-xs" style={{ color: palette.text.muted }}>{label}</span>
    <div className="flex items-center gap-1">
      <span style={{ color: palette.text.muted }}>{icon}</span>
      <span className="text-xs font-semibold" style={{ color }}>{value}</span>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// RecommendationPanel — compact chip strip
// ─────────────────────────────────────────────────────────────────────────────
const REC_COLOR = {
  cost:    palette.accents.warm.base,
  latency: palette.accents.secondary.base,
  quality: palette.status.success,
};
const REC_ICON = {
  cost:    <DollarSign size={11} />,
  latency: <Zap size={11} />,
  quality: <Sparkles size={11} />,
};

// Build a human-readable label for a manual override
function overrideLabel(spanId, override, spans) {
  const span = spans.find(s => s.span_id === spanId);
  const parts = [];
  if (override.model && span?.llm_model_name && override.model !== span.llm_model_name)
    parts.push(`${span.llm_model_name} → ${override.model}`);
  if (override.temperature !== undefined && span?.temperature !== undefined && override.temperature !== span.temperature)
    parts.push(`temp ${span.temperature}→${override.temperature}`);
  if (override.max_tokens !== undefined && override.max_tokens !== span?.max_tokens)
    parts.push(`max_tokens ${override.max_tokens}`);
  if (override.system_prompt !== undefined && override.system_prompt !== span?.system_prompt)
    parts.push('prompt edited');
  if (override.top_k !== undefined && span?.retriever_config?.top_k !== undefined && override.top_k !== span.retriever_config.top_k)
    parts.push(`top_k ${span.retriever_config.top_k}→${override.top_k}`);
  if (override.similarity_threshold !== undefined)
    parts.push(`threshold ${override.similarity_threshold}`);
  if (override.schema !== undefined) parts.push('schema edited');
  return parts.length ? parts.join(', ') : 'modified';
}

const RecommendationPanel = ({ recommendations, checkedIds, onToggle, manualOverrides, spans, onClearManual, onCustomize, onView, onRun, runStatus }) => {
  const totalChanges = checkedIds.size + Object.keys(manualOverrides).length;

  return (
  <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${palette.borders.medium}` }}>
    {/* Title row */}
    <div
      className="flex items-center justify-between px-4 py-2.5"
      style={{ borderBottom: `1px solid ${palette.borders.subtle}`, backgroundColor: withOpacity(palette.accents.primary.base, 4) }}
    >
      <div className="flex items-center gap-2">
        <Sparkles size={13} style={{ color: palette.accents.primary.base }} />
        <span className="text-xs font-semibold" style={{ color: palette.text.primary }}>Simulation Changes</span>
        {totalChanges > 0 && (
          <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: withOpacity(palette.status.success, 15), color: palette.status.success }}>
            {totalChanges} change{totalChanges !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onCustomize}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all"
          style={{ border: `1px solid ${palette.borders.medium}`, color: palette.text.supporting, backgroundColor: 'transparent', cursor: 'pointer' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = palette.accents.secondary.base; e.currentTarget.style.color = palette.accents.secondary.base; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = palette.borders.medium; e.currentTarget.style.color = palette.text.supporting; }}
        >
          <Pencil size={11} /> Customize
        </button>
        <button
        onClick={onRun}
        disabled={totalChanges === 0 || runStatus === 'running'}
        className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-all"
        style={{
          backgroundColor: totalChanges > 0 ? palette.accents.primary.base : palette.neutrals.surfaceMid,
          color: totalChanges > 0 ? palette.text.inverse : palette.text.muted,
          cursor: totalChanges > 0 ? 'pointer' : 'not-allowed',
          opacity: runStatus === 'running' ? 0.7 : 1,
        }}
      >
        {runStatus === 'running' ? (
          <><div className="w-3 h-3 rounded-full border-2 animate-spin" style={{ borderColor: withOpacity(palette.text.inverse, 40), borderTopColor: palette.text.inverse }} /> Running…</>
        ) : (
          <><Play size={11} fill="currentColor" /> Run Simulation</>
        )}
        </button>
      </div>
    </div>

    {/* Chips */}
    <div className="flex items-center gap-2 flex-wrap px-4 py-3">
    {recommendations.map(rec => {
      const checked = checkedIds.has(rec.id);
      const typeColor = REC_COLOR[rec.type];
      const spanKind = rec.span_id === 's2' ? 'RETRIEVER' : rec.span_id === 's3' ? 'TOOL' : 'LLM';
      const spanColor = kindConfig(spanKind).color;

      return (
        <button
          key={rec.id}
          onClick={() => onToggle(rec)}
          title={rec.tooltip}
          className="flex items-center gap-2 transition-all"
          style={{
            height: 32,
            padding: '0 10px 0 8px',
            borderRadius: 20,
            border: `1px solid ${checked ? typeColor : palette.borders.medium}`,
            backgroundColor: checked ? withOpacity(typeColor, 14) : palette.neutrals.surfaceSubtle,
            cursor: 'pointer',
            outline: 'none',
          }}
          onMouseEnter={e => { if (!checked) { e.currentTarget.style.borderColor = typeColor; e.currentTarget.style.backgroundColor = withOpacity(typeColor, 8); }}}
          onMouseLeave={e => { if (!checked) { e.currentTarget.style.borderColor = palette.borders.medium; e.currentTarget.style.backgroundColor = palette.neutrals.surfaceSubtle; }}}
        >
          {/* Checkbox dot */}
          <div
            className="flex items-center justify-center rounded-full shrink-0"
            style={{
              width: 14, height: 14,
              border: `1.5px solid ${checked ? typeColor : palette.borders.strong}`,
              backgroundColor: checked ? typeColor : 'transparent',
            }}
          >
            {checked && <Check size={8} color={palette.text.inverse} strokeWidth={3} />}
          </div>

          {/* Type icon */}
          <span style={{ color: typeColor, display: 'flex', alignItems: 'center' }}>{REC_ICON[rec.type]}</span>

          {/* Action label */}
          <span
            className="text-xs font-mono"
            style={{ color: palette.text.primary, whiteSpace: 'nowrap' }}
          >
            {rec.action_label}
          </span>

          {/* Span name */}
          <span
            className="text-xs px-1.5 py-0.5 rounded shrink-0"
            style={{ backgroundColor: `${spanColor}14`, color: spanColor, fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            {rec.span_name}
          </span>

          {/* Impact badges */}
          <div className="flex items-center gap-1 shrink-0">
            {rec.impact.cost !== 0 && (
              <span className="text-xs font-semibold" style={{ color: rec.impact.cost < 0 ? palette.status.success : palette.status.danger, fontSize: 11 }}>
                {deltaPrefix(rec.impact.cost)}{rec.impact.cost}%💰
              </span>
            )}
            {rec.impact.latency !== 0 && (
              <span className="text-xs font-semibold" style={{ color: rec.impact.latency < 0 ? palette.status.success : palette.status.danger, fontSize: 11 }}>
                {deltaPrefix(rec.impact.latency)}{rec.impact.latency}%⚡
              </span>
            )}
            {rec.impact.quality !== 0 && (
              <span className="text-xs font-semibold" style={{ color: rec.impact.quality > 0 ? palette.status.success : palette.status.danger, fontSize: 11 }}>
                {deltaPrefix(rec.impact.quality)}{rec.impact.quality}%✨
              </span>
            )}
          </div>

          {/* View details */}
          <button
            onClick={e => { e.stopPropagation(); onView(rec); }}
            className="flex items-center justify-center rounded-full shrink-0 transition-colors"
            style={{ width: 20, height: 20, color: palette.text.muted, marginLeft: 2 }}
            title="View details"
            onMouseEnter={e => e.currentTarget.style.color = palette.accents.secondary.base}
            onMouseLeave={e => e.currentTarget.style.color = palette.text.muted}
          >
            <Eye size={11} />
          </button>
        </button>
      );
    })}

      {/* Divider + manual override chips */}
      {Object.keys(manualOverrides).length > 0 && (
        <>
          <div style={{ width: 1, height: 20, backgroundColor: palette.borders.medium, flexShrink: 0, margin: '0 4px' }} />
          <span className="text-xs shrink-0" style={{ color: palette.text.muted }}>Your changes</span>
          {Object.entries(manualOverrides).map(([spanId, override]) => {
            const span = spans.find(s => s.span_id === spanId);
            const cfg = kindConfig(span?.span_kind || 'LLM');
            const label = overrideLabel(spanId, override, spans);
            return (
              <div
                key={spanId}
                className="flex items-center gap-2"
                style={{
                  height: 32, padding: '0 8px 0 10px', borderRadius: 20,
                  border: `1px solid ${cfg.color}60`,
                  backgroundColor: `${cfg.color}10`,
                  flexShrink: 0,
                }}
              >
                <span className="text-xs font-mono" style={{ color: palette.text.primary, whiteSpace: 'nowrap' }}>{label}</span>
                <span className="text-xs px-1.5 py-0.5 rounded shrink-0" style={{ backgroundColor: `${cfg.color}14`, color: cfg.color, fontSize: 10, fontWeight: 600 }}>
                  {span?.name || spanId}
                </span>
                <button onClick={() => onClearManual(spanId)} style={{ color: palette.text.muted, display: 'flex', alignItems: 'center', marginLeft: 2 }} title="Remove">
                  <X size={11} />
                </button>
              </div>
            );
          })}
        </>
      )}

      {totalChanges === 0 && (
        <span className="text-xs" style={{ color: palette.text.muted }}>
          Check a recommendation or click a span to configure changes
        </span>
      )}
    </div>
  </div>
  );
};

const ImpactBadge = ({ icon, val, suffix }) => {
  const color = val < 0 ? palette.status.success : palette.status.danger;
  const bg = val < 0 ? withOpacity(palette.status.success, 12) : withOpacity(palette.status.danger, 12);
  const Arrow = val < 0 ? TrendingDown : TrendingUp;
  return (
    <span className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: bg, color }}>
      <Arrow size={9} />
      <span style={{ color: palette.text.muted }}>{icon}</span>
      {deltaPrefix(val)}{val}{suffix}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// WaterfallTree
// ─────────────────────────────────────────────────────────────────────────────
const INDENT_PX = 18;
const ROW_H = 38;
const BAR_AREA_W = 220; // px reserved for waterfall bars on the right

const WaterfallTree = ({ spans, selectedSpanId, onSelectSpan, activeOverrides }) => {
  const totalDuration = useMemo(() => {
    const maxEnd = Math.max(...spans.map(s => s.start_ms + s.duration_ms));
    return maxEnd;
  }, [spans]);

  const depths = useMemo(() => computeDepths(spans), [spans]);
  const [collapsed, setCollapsed] = useState(new Set());

  // Compute visible spans (respecting collapsed state)
  const visibleSpans = useMemo(() => {
    const hidden = new Set();
    return spans.filter(span => {
      if (hidden.has(span.span_id)) return false;
      if (collapsed.has(span.span_id)) {
        // hide all descendants
        const queue = [span.span_id];
        while (queue.length) {
          const pid = queue.shift();
          spans.forEach(s => { if (s.parent_span_id === pid) { hidden.add(s.span_id); queue.push(s.span_id); } });
        }
      }
      return true;
    });
  }, [spans, collapsed]);

  const hasChildren = useMemo(() => {
    const set = new Set();
    spans.forEach(s => { if (s.parent_span_id) set.add(s.parent_span_id); });
    return set;
  }, [spans]);

  const toggleCollapse = useCallback((spanId, e) => {
    e.stopPropagation();
    setCollapsed(prev => {
      const next = new Set(prev);
      next.has(spanId) ? next.delete(spanId) : next.add(spanId);
      return next;
    });
  }, []);

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${palette.borders.medium}` }}>
      {/* Header row */}
      <div className="flex items-center px-4 py-2 text-xs font-medium uppercase tracking-wider" style={{ backgroundColor: palette.neutrals.surfaceSubtle, color: palette.text.muted, borderBottom: `1px solid ${palette.borders.subtle}` }}>
        <span style={{ flex: 1 }}>Span</span>
        <span style={{ width: 60, textAlign: 'right' }}>Tokens</span>
        <span style={{ width: 56, textAlign: 'right' }}>Cost</span>
        <span style={{ width: 52, textAlign: 'right' }}>Latency</span>
        <span style={{ width: BAR_AREA_W, textAlign: 'left', paddingLeft: 12 }}>Timeline</span>
      </div>

      {/* Span rows */}
      {visibleSpans.map(span => {
        const cfg = kindConfig(span.span_kind);
        const depth = depths[span.span_id] || 0;
        const isSelected = span.span_id === selectedSpanId;
        const hasOverride = activeOverrides.has(span.span_id);
        const isCollapsed = collapsed.has(span.span_id);
        const hasKids = hasChildren.has(span.span_id);
        const tokens = (span.prompt_tokens || 0) + (span.completion_tokens || 0);

        // Waterfall bar geometry
        const offsetPct = (span.start_ms / totalDuration) * 100;
        const widthPct = Math.max((span.duration_ms / totalDuration) * 100, 0.5);

        return (
          <div
            key={span.span_id}
            onClick={() => cfg.editable && onSelectSpan(span)}
            className="flex items-center group transition-all"
            style={{
              height: ROW_H,
              paddingLeft: 16 + depth * INDENT_PX,
              paddingRight: 0,
              backgroundColor: isSelected
                ? withOpacity(cfg.color, 12)
                : hasOverride
                  ? withOpacity(cfg.color, 8)
                  : 'transparent',
              borderLeft: isSelected
                ? `2px solid ${cfg.color}`
                : hasOverride
                  ? `2px solid ${withOpacity(cfg.color, 60)}`
                  : '2px solid transparent',
              cursor: cfg.editable ? 'pointer' : 'default',
              borderBottom: `1px solid ${palette.borders.subtle}`,
            }}
            onMouseEnter={e => { if (!isSelected && cfg.editable) e.currentTarget.style.backgroundColor = palette.neutrals.surfaceSubtle; }}
            onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = hasOverride ? withOpacity(cfg.color, 8) : 'transparent'; }}
          >
            {/* Collapse toggle */}
            <div style={{ width: 14, marginRight: 4, flexShrink: 0 }}>
              {hasKids && (
                <button
                  onClick={e => toggleCollapse(span.span_id, e)}
                  style={{ color: palette.text.muted, lineHeight: 1 }}
                >
                  {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                </button>
              )}
            </div>

            {/* Type badge */}
            <span
              className="text-xs font-semibold rounded px-1.5 py-0.5 shrink-0 mr-2"
              style={{ backgroundColor: cfg.bg, color: cfg.color, fontSize: 10, letterSpacing: '0.3px' }}
            >
              {cfg.label}
            </span>

            {/* Span name */}
            <span
              className="text-sm truncate flex-1 min-w-0"
              style={{ color: isSelected ? cfg.color : palette.text.primary, fontWeight: isSelected ? 600 : 400 }}
            >
              {span.name}
            </span>

            {/* Edit + override indicators */}
            <div className="flex items-center gap-1.5 shrink-0 mx-2">
              {cfg.editable && (
                <Pencil
                  size={11}
                  style={{ color: isSelected ? cfg.color : palette.text.muted, opacity: isSelected ? 1 : 0 }}
                  className="group-hover:opacity-100 transition-opacity"
                />
              )}
              {hasOverride && (
                <span className="text-xs rounded px-1 py-0.5" style={{ backgroundColor: withOpacity(cfg.color, 20), color: cfg.color, fontSize: 9, fontWeight: 700 }}>
                  OVERRIDE
                </span>
              )}
            </div>

            {/* Tokens */}
            <span style={{ width: 60, textAlign: 'right', flexShrink: 0, color: tokens ? palette.text.supporting : palette.text.muted, fontSize: 12 }}>
              {tokens ? `${(tokens / 1000).toFixed(1)}k` : '—'}
            </span>

            {/* Cost */}
            <span style={{ width: 56, textAlign: 'right', flexShrink: 0, color: span.cost_usd ? palette.accents.warm.base : palette.text.muted, fontSize: 12, paddingRight: 4 }}>
              {span.cost_usd ? fmtCost(span.cost_usd) : '—'}
            </span>

            {/* Latency */}
            <span style={{ width: 52, textAlign: 'right', flexShrink: 0, color: palette.text.supporting, fontSize: 12, paddingRight: 8 }}>
              {fmtMs(span.duration_ms)}
            </span>

            {/* Waterfall bar */}
            <div style={{ width: BAR_AREA_W, flexShrink: 0, padding: '0 12px', position: 'relative', height: '100%', display: 'flex', alignItems: 'center' }}>
              <div style={{ position: 'relative', width: '100%', height: 8, backgroundColor: palette.neutrals.surfaceSubtle, borderRadius: 4, overflow: 'hidden' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: `${offsetPct}%`,
                    width: `${widthPct}%`,
                    height: '100%',
                    backgroundColor: hasOverride ? cfg.color : withOpacity(cfg.color, 60),
                    borderRadius: 4,
                    boxShadow: hasOverride ? `0 0 6px ${withOpacity(cfg.color, 60)}` : 'none',
                    transition: 'background-color 0.2s',
                  }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// OverrideEditorPanel
// ─────────────────────────────────────────────────────────────────────────────
const OverrideEditorPanel = ({ span, override, onChange, onClear }) => {
  const cfg = span ? kindConfig(span.span_kind) : null;

  if (!span) {
    return (
      <div className="rounded-xl flex flex-col items-center justify-center gap-3 h-full" style={{ border: `1px dashed ${palette.borders.medium}`, minHeight: 200 }}>
        <Pencil size={20} style={{ color: palette.text.muted }} />
        <p className="text-sm" style={{ color: palette.text.muted }}>Select an editable span to configure overrides</p>
        <p className="text-xs" style={{ color: palette.text.muted }}>LLM, RETRIEVER and TOOL spans support overrides</p>
      </div>
    );
  }

  const isHarnessOnly = span.span_kind === 'RETRIEVER' || span.span_kind === 'TOOL';

  return (
    <div className="rounded-xl flex flex-col overflow-hidden" style={{ border: `1px solid ${withOpacity(cfg.color, 40)}` }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3" style={{ backgroundColor: withOpacity(cfg.color, 10), borderBottom: `1px solid ${withOpacity(cfg.color, 20)}` }}>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold rounded px-1.5 py-0.5" style={{ backgroundColor: cfg.bg, color: cfg.color }}>
            {cfg.label}
          </span>
          <span className="text-sm font-semibold" style={{ color: palette.text.primary }}>{span.name}</span>
        </div>
        {override && Object.keys(override).length > 0 && (
          <button
            onClick={onClear}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded"
            style={{ color: palette.text.muted, border: `1px solid ${palette.borders.subtle}` }}
          >
            <RotateCcw size={10} /> Reset
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
        {/* Harness-only notice */}
        {isHarnessOnly && (
          <div className="flex items-start gap-2 rounded-lg p-3" style={{ backgroundColor: withOpacity(palette.accents.secondary.base, 8), border: `1px solid ${withOpacity(palette.accents.secondary.base, 20)}` }}>
            <AlertTriangle size={13} style={{ color: palette.accents.secondary.base, marginTop: 1, flexShrink: 0 }} />
            <p className="text-xs leading-relaxed" style={{ color: palette.text.supporting }}>
              Config overrides for {cfg.label} spans are stored now and applied automatically when running in <strong>Full Application mode</strong>. They are not re-executed in Replay mode.
            </p>
          </div>
        )}

        {/* LLM Controls */}
        {span.span_kind === 'LLM' && (
          <>
            {/* Model selector */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium" style={{ color: palette.text.supporting }}>Model</label>
              <select
                value={override?.model || span.llm_model_name || ''}
                onChange={e => onChange({ ...override, model: e.target.value, provider: MOCK_MODELS.find(m => m.id === e.target.value)?.provider || span.llm_provider })}
                className="rounded-lg px-3 py-2 text-sm w-full"
                style={{ backgroundColor: palette.neutrals.surfaceSubtle, border: `1px solid ${palette.borders.medium}`, color: palette.text.primary, outline: 'none' }}
              >
                {MOCK_MODELS.map(m => (
                  <option key={m.id} value={m.id} style={{ backgroundColor: palette.neutrals.panelSecondary }}>
                    {m.label} ({m.provider}) — ${m.cost_per_1m_in}/1M in
                  </option>
                ))}
              </select>
              {/* Cost comparison */}
              {override?.model && override.model !== span.llm_model_name && (() => {
                const orig = MOCK_MODELS.find(m => m.id === span.llm_model_name);
                const next = MOCK_MODELS.find(m => m.id === override.model);
                if (!orig || !next) return null;
                const savePct = Math.round((1 - (next.cost_per_1m_in + next.cost_per_1m_out) / (orig.cost_per_1m_in + orig.cost_per_1m_out)) * 100);
                return (
                  <div className="flex items-center gap-1.5 text-xs" style={{ color: savePct > 0 ? palette.status.success : palette.status.danger }}>
                    {savePct > 0 ? <TrendingDown size={11} /> : <TrendingUp size={11} />}
                    Estimated {Math.abs(savePct)}% {savePct > 0 ? 'cost reduction' : 'cost increase'} vs {span.llm_model_name}
                  </div>
                );
              })()}
            </div>

            {/* System prompt */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium" style={{ color: palette.text.supporting }}>System Prompt</label>
              <textarea
                value={override?.system_prompt !== undefined ? override.system_prompt : (span.system_prompt || '')}
                onChange={e => onChange({ ...override, system_prompt: e.target.value })}
                rows={5}
                className="rounded-lg px-3 py-2 text-xs w-full resize-none"
                style={{ backgroundColor: palette.neutrals.surfaceSubtle, border: `1px solid ${palette.borders.medium}`, color: palette.text.primary, fontFamily: 'monospace', outline: 'none', lineHeight: 1.6 }}
              />
            </div>

            {/* Temperature + max_tokens */}
            <div className="flex gap-3">
              <div className="flex flex-col gap-1.5 flex-1">
                <label className="text-xs font-medium" style={{ color: palette.text.supporting }}>Temperature</label>
                <div className="flex items-center gap-2">
                  <input
                    type="range" min="0" max="2" step="0.1"
                    value={override?.temperature !== undefined ? override.temperature : (span.temperature ?? 1.0)}
                    onChange={e => onChange({ ...override, temperature: parseFloat(e.target.value) })}
                    className="flex-1"
                    style={{ accentColor: cfg.color }}
                  />
                  <span className="text-xs font-mono w-8" style={{ color: palette.text.primary }}>
                    {(override?.temperature !== undefined ? override.temperature : (span.temperature ?? 1.0)).toFixed(1)}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-1.5" style={{ width: 96 }}>
                <label className="text-xs font-medium" style={{ color: palette.text.supporting }}>Max Tokens</label>
                <input
                  type="number"
                  value={override?.max_tokens !== undefined ? override.max_tokens : (span.max_tokens || '')}
                  onChange={e => onChange({ ...override, max_tokens: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="auto"
                  className="rounded-lg px-2 py-1.5 text-xs w-full"
                  style={{ backgroundColor: palette.neutrals.surfaceSubtle, border: `1px solid ${palette.borders.medium}`, color: palette.text.primary, outline: 'none' }}
                />
              </div>
            </div>
          </>
        )}

        {/* RETRIEVER Controls */}
        {span.span_kind === 'RETRIEVER' && (
          <>
            <div className="flex gap-3">
              <div className="flex flex-col gap-1.5 flex-1">
                <label className="text-xs font-medium" style={{ color: palette.text.supporting }}>top_k</label>
                <div className="flex items-center gap-2">
                  <input type="range" min="1" max="20" step="1"
                    value={override?.top_k !== undefined ? override.top_k : (span.retriever_config?.top_k || 5)}
                    onChange={e => onChange({ ...override, top_k: parseInt(e.target.value) })}
                    className="flex-1" style={{ accentColor: cfg.color }}
                  />
                  <span className="text-xs font-mono w-6" style={{ color: palette.text.primary }}>
                    {override?.top_k !== undefined ? override.top_k : (span.retriever_config?.top_k || 5)}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-1.5 flex-1">
                <label className="text-xs font-medium" style={{ color: palette.text.supporting }}>Similarity Threshold</label>
                <div className="flex items-center gap-2">
                  <input type="range" min="0" max="1" step="0.05"
                    value={override?.similarity_threshold !== undefined ? override.similarity_threshold : (span.retriever_config?.similarity_threshold || 0.7)}
                    onChange={e => onChange({ ...override, similarity_threshold: parseFloat(e.target.value) })}
                    className="flex-1" style={{ accentColor: cfg.color }}
                  />
                  <span className="text-xs font-mono w-8" style={{ color: palette.text.primary }}>
                    {(override?.similarity_threshold !== undefined ? override.similarity_threshold : (span.retriever_config?.similarity_threshold || 0.7)).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium" style={{ color: palette.text.supporting }}>Embedding Model</label>
              <select
                value={override?.embedding_model || span.retriever_config?.embedding_model || ''}
                onChange={e => onChange({ ...override, embedding_model: e.target.value })}
                className="rounded-lg px-3 py-2 text-sm w-full"
                style={{ backgroundColor: palette.neutrals.surfaceSubtle, border: `1px solid ${palette.borders.medium}`, color: palette.text.primary, outline: 'none' }}
              >
                {['text-embedding-3-small', 'text-embedding-3-large', 'text-embedding-ada-002'].map(m => (
                  <option key={m} value={m} style={{ backgroundColor: palette.neutrals.panelSecondary }}>{m}</option>
                ))}
              </select>
            </div>
          </>
        )}

        {/* TOOL Controls */}
        {span.span_kind === 'TOOL' && span.tool_schema && (
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium" style={{ color: palette.text.supporting }}>Tool Schema (JSON)</label>
            <textarea
              value={override?.schema ? JSON.stringify(override.schema, null, 2) : JSON.stringify(span.tool_schema, null, 2)}
              onChange={e => { try { onChange({ ...override, schema: JSON.parse(e.target.value) }); } catch {} }}
              rows={8}
              className="rounded-lg px-3 py-2 text-xs w-full resize-none font-mono"
              style={{ backgroundColor: palette.neutrals.surfaceSubtle, border: `1px solid ${palette.borders.medium}`, color: palette.support?.codeText || palette.accents.secondary.base, outline: 'none', lineHeight: 1.6 }}
            />
          </div>
        )}

        {/* Original metrics */}
        {(span.quality || span.prompt_tokens) && (
          <div className="rounded-lg p-3 flex flex-wrap gap-3" style={{ backgroundColor: palette.neutrals.surfaceSubtle, border: `1px solid ${palette.borders.subtle}` }}>
            <span className="text-xs font-medium w-full" style={{ color: palette.text.muted }}>Original metrics</span>
            {span.prompt_tokens > 0 && <OrigMetric label="Tokens" value={`${span.prompt_tokens + span.completion_tokens}`} />}
            {span.cost_usd && <OrigMetric label="Cost" value={fmtCost(span.cost_usd)} />}
            {span.quality?.answer_relevance !== undefined && <OrigMetric label="Answer Rel." value={(span.quality.answer_relevance * 100).toFixed(0) + '%'} />}
            {span.quality?.faithfulness !== undefined && <OrigMetric label="Faithfulness" value={(span.quality.faithfulness * 100).toFixed(0) + '%'} />}
            {span.quality?.context_relevance !== undefined && <OrigMetric label="Context Rel." value={(span.quality.context_relevance * 100).toFixed(0) + '%'} />}
          </div>
        )}
      </div>
    </div>
  );
};

const OrigMetric = ({ label, value }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs" style={{ color: palette.text.muted, fontSize: 10 }}>{label}</span>
    <span className="text-xs font-mono" style={{ color: palette.text.supporting }}>{value}</span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// ResultsPanel
// ─────────────────────────────────────────────────────────────────────────────
const ResultsPanel = ({ workflow, hasResults, runStatus, setShowClaudeCodeModal, setShowLinearModal }) => {
  const results = {
    original: { cost: workflow.avg_cost_usd, latency_ms: workflow.avg_latency_ms, quality: workflow.avg_quality },
    replay:   { cost: workflow.avg_cost_usd * 0.32, latency_ms: workflow.avg_latency_ms * 0.61, quality: workflow.avg_quality * 1.05 },
    spans: [
      { name: 'ChatCompletion',   original_model: 'gpt-4o', replay_model: 'gpt-4o-mini', cost_delta: -68, latency_delta: -22 },
      { name: 'retrieve_context', original_k: 10,           replay_k: 5,                 cost_delta: 0,   latency_delta: -38 },
    ],
  };

  const costDelta = Math.round(((results.replay.cost - results.original.cost) / results.original.cost) * 100);
  const latDelta  = Math.round(((results.replay.latency_ms - results.original.latency_ms) / results.original.latency_ms) * 100);
  const qualDelta = Math.round(((results.replay.quality - results.original.quality) / results.original.quality) * 100);

  return (
    <div className="rounded-xl overflow-hidden flex-1" style={{ border: `1px solid ${palette.borders.medium}` }}>
      {/* Header */}
      <div
        className="flex items-center gap-2 px-4 py-3"
        style={{
          borderBottom: `1px solid ${palette.borders.subtle}`,
          backgroundColor: hasResults ? withOpacity(palette.status.success, 6) : palette.neutrals.surfaceSubtle,
        }}
      >
        {hasResults
          ? <CheckCircle size={14} style={{ color: palette.status.success }} />
          : <Play size={13} style={{ color: palette.text.muted }} />}
        <span className="text-sm font-semibold" style={{ color: palette.text.primary }}>Simulation Results</span>
        {hasResults && (
          <span className="text-xs" style={{ color: palette.text.muted }}>— estimated across {workflow.trace_count} traces</span>
        )}
      </div>

      {/* Empty state */}
      {!hasResults && (
        <div className="flex flex-col items-center justify-center gap-3 py-10 px-6 text-center">
          {runStatus === 'running' ? (
            <>
              <div className="w-6 h-6 rounded-full border-2 animate-spin" style={{ borderColor: withOpacity(palette.accents.primary.base, 30), borderTopColor: palette.accents.primary.base }} />
              <p className="text-sm" style={{ color: palette.text.supporting }}>Running simulation…</p>
            </>
          ) : (
            <>
              <div className="rounded-full p-3" style={{ backgroundColor: palette.neutrals.surfaceSubtle }}>
                <Play size={20} style={{ color: palette.text.muted }} />
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: palette.text.supporting }}>No results yet</p>
                <p className="text-xs mt-1 leading-relaxed" style={{ color: palette.text.muted }}>
                  Apply recommendations or customize spans above,<br />then click <strong style={{ color: palette.accents.primary.base }}>Run Simulation</strong> to see estimated impact.
                </p>
              </div>
              <div className="flex items-center gap-4 mt-1">
                <div className="flex items-center gap-1.5 text-xs" style={{ color: palette.text.muted }}>
                  <DollarSign size={11} /> Cost delta
                </div>
                <div className="flex items-center gap-1.5 text-xs" style={{ color: palette.text.muted }}>
                  <Clock size={11} /> Latency delta
                </div>
                <div className="flex items-center gap-1.5 text-xs" style={{ color: palette.text.muted }}>
                  <Sparkles size={11} /> Quality delta
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Results */}
      {hasResults && (
        <div className="p-4 flex flex-col gap-4">
          <div className="grid grid-cols-3 gap-3">
            <ResultMetricCard label="Cost" original={`$${results.original.cost.toFixed(4)}`} replay={`$${results.replay.cost.toFixed(4)}`} delta={costDelta} icon={<DollarSign size={13} />} />
            <ResultMetricCard label="Latency" original={fmtMs(results.original.latency_ms)} replay={fmtMs(results.replay.latency_ms)} delta={latDelta} icon={<Clock size={13} />} />
            <ResultMetricCard label="Quality" original={(results.original.quality * 100).toFixed(0) + '%'} replay={(results.replay.quality * 100).toFixed(0) + '%'} delta={qualDelta} icon={<Sparkles size={13} />} invert />
          </div>
          <div>
            <p className="text-xs font-medium mb-2" style={{ color: palette.text.muted }}>Per-span changes</p>
            {results.spans.map((s, i) => (
              <div key={i} className="flex items-center gap-3 py-2" style={{ borderTop: `1px solid ${palette.borders.subtle}` }}>
                <span className="text-sm flex-1" style={{ color: palette.text.primary }}>{s.name}</span>
                {s.original_model && (
                  <span className="text-xs" style={{ color: palette.text.muted }}>
                    {s.original_model} → <span style={{ color: palette.accents.primary.base }}>{s.replay_model}</span>
                  </span>
                )}
                {s.original_k && (
                  <span className="text-xs" style={{ color: palette.text.muted }}>
                    top_k {s.original_k} → <span style={{ color: palette.status.success }}>{s.replay_k}</span>
                  </span>
                )}
                {s.cost_delta !== 0 && <ImpactBadge icon={<DollarSign size={9} />} val={s.cost_delta} suffix="%" />}
                {s.latency_delta !== 0 && <ImpactBadge icon={<Clock size={9} />} val={s.latency_delta} suffix="%" />}
              </div>
            ))}
          </div>
          <p className="text-xs" style={{ color: palette.text.muted }}>
            Estimates based on model pricing and historical quality scores. Run in Full Application mode for exact measurements.
          </p>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-3" style={{ borderTop: `1px solid ${palette.borders.subtle}` }}>
            <button
              onClick={() => setShowClaudeCodeModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-xs rounded-md transition-colors"
              style={{
                backgroundColor: 'var(--color-surface-subtle)',
                color: palette.accents.primary.base,
                border: `1px solid ${palette.accents.primary.base}40`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = withOpacity(palette.accents.primary.base, 6);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-surface-subtle)';
              }}
            >
              <Zap size={13} />
              Code Fix with Claude
            </button>
            <button
              onClick={() => setShowLinearModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-xs rounded-md transition-colors"
              style={{
                backgroundColor: 'var(--color-surface-subtle)',
                color: palette.accents.secondary.base,
                border: `1px solid ${palette.accents.secondary.base}40`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = withOpacity(palette.accents.secondary.base, 6);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-surface-subtle)';
              }}
            >
              <FileText size={13} />
              Create Linear Issue
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Claude Code Modal
// ─────────────────────────────────────────────────────────────────────────────
const ClaudeCodeModal = ({ workflow, onClose }) => {
  const [mcpCopied, setMcpCopied] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);

  const handleCopyMCP = async () => {
    const mcpConfig = `"lumoz": { "command": "lumoz-mcp", "env": { "LUMOZ_API_KEY": "id:secret" } }`;
    await navigator.clipboard.writeText(`{\n  "mcpServers": {\n    ${mcpConfig}\n  }\n}`);
    setMcpCopied(true);
    setTimeout(() => setMcpCopied(false), 2000);
  };

  const handleCopyPrompt = async () => {
    const claudePrompt = `Use Lumoz MCP to apply simulation optimizations for business task: ${workflow?.name}\n\nApply the recommended changes to reduce cost by 68% and latency by 39% while improving quality by 5%.`;
    await navigator.clipboard.writeText(claudePrompt);
    setPromptCopied(true);
    setTimeout(() => setPromptCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ backgroundColor: palette.quality.overlayModal }}>
      <div
        className="relative flex flex-col rounded-xl w-[900px]"
        style={{
          backgroundColor: 'var(--color-card-bg)',
          border: '1px solid var(--color-border-subtle)',
          boxShadow: 'var(--shadow-dropdown)',
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4"
          style={{ borderBottom: `1px solid ${palette.borders.subtle}` }}>
          <div className="flex items-center gap-2">
            <ExternalLink size={18} style={{ color: palette.accents.primary.base }} />
            <h3 className="text-base font-semibold" style={{ color: palette.text.primary }}>Apply with Claude Code</h3>
          </div>
          <button onClick={onClose} className="hover:opacity-70">
            <X size={18} style={{ color: palette.text.muted }} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-2 gap-6">
            {/* Step 1: MCP Config */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold"
                  style={{ backgroundColor: palette.accents.primary.base, color: palette.text.inverse }}>1</div>
                <span className="text-sm font-medium" style={{ color: palette.text.primary }}>Configure MCP Server</span>
                <span className="text-[10px] px-2 py-0.5 rounded"
                  style={{ backgroundColor: 'var(--color-surface-hover)', color: palette.text.muted }}>One-time</span>
              </div>
              <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--color-border-subtle)' }}>
                <div className="px-3 py-2 text-xs flex items-center justify-between"
                  style={{ backgroundColor: 'var(--color-table-header-bg)', color: palette.text.muted }}>
                  <span>~/.claude/settings.json</span>
                  <button onClick={handleCopyMCP} className="flex items-center gap-1 hover:opacity-80">
                    {mcpCopied ? <Check size={14} /> : <Copy size={14} />}
                    <span className="text-[10px]">{mcpCopied ? 'Copied!' : 'Copy'}</span>
                  </button>
                </div>
                <pre className="p-3 text-xs font-mono"
                  style={{ backgroundColor: 'var(--color-input-bg)', color: palette.text.primary }}>
                  {"\"lumoz\": {\n  \"command\": \"lumoz-mcp\",\n  \"env\": {\n    \"LUMOZ_API_KEY\": \"id:secret\"\n  }\n}"}
                </pre>
              </div>
              <p className="text-xs mt-2" style={{ color: palette.text.muted }}>
                Install: <code className="px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--color-table-header-bg)' }}>pip install lumoz-sdk[mcp]</code>
              </p>
            </div>

            {/* Step 2: Claude Prompt */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold"
                  style={{ backgroundColor: palette.accents.primary.base, color: palette.text.inverse }}>2</div>
                <span className="text-sm font-medium" style={{ color: palette.text.primary }}>Run in Claude Code</span>
              </div>
              <div className="rounded-lg overflow-hidden"
                style={{ border: `1px solid ${palette.accents.primary.base}` }}>
                <div className="px-3 py-2 text-xs flex items-center justify-between"
                  style={{ backgroundColor: withOpacity(palette.accents.primary.base, 15), color: palette.accents.primary.base }}>
                  <span>Prompt</span>
                  <button onClick={handleCopyPrompt} className="flex items-center gap-1 hover:opacity-80">
                    {promptCopied ? <Check size={14} /> : <Copy size={14} />}
                    <span className="text-[10px]">{promptCopied ? 'Copied!' : 'Copy'}</span>
                  </button>
                </div>
                <div className="p-3 text-xs"
                  style={{ backgroundColor: 'var(--color-input-bg)', color: palette.text.primary }}>
                  Use Lumoz MCP to apply simulation optimizations for business task: <strong>{workflow?.name}</strong><br /><br />
                  Apply the recommended changes to reduce cost by 68% and latency by 39% while improving quality by 5%.
                </div>
              </div>
              <p className="text-xs mt-2" style={{ color: palette.text.muted }}>
                Claude will use Lumoz MCP to fetch simulation details and apply the optimizations
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4"
          style={{ borderTop: `1px solid ${palette.borders.subtle}` }}>
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs rounded-md transition-colors"
            style={{ color: palette.text.supporting, backgroundColor: 'var(--color-surface-hover)' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Linear Issue Modal
// ─────────────────────────────────────────────────────────────────────────────
const LinearIssueModal = ({ workflow, onClose }) => {
  const [issueCopied, setIssueCopied] = useState(false);

  const handleCopyIssue = async () => {
    const issueTemplate = `**Business Task**: ${workflow?.name}\n\n**Optimization Opportunity**:\n- Cost reduction: 68%\n- Latency reduction: 39%\n- Quality improvement: 5%\n\n**Recommended Changes**:\nApply Lumoz simulation recommendations to optimize model selection and retrieval parameters.\n\n**Impact**: Estimated savings of $${(workflow?.avg_cost_usd * workflow?.trace_count * 0.68).toFixed(2)} per month based on ${workflow?.trace_count} traces.`;
    await navigator.clipboard.writeText(issueTemplate);
    setIssueCopied(true);
    setTimeout(() => setIssueCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ backgroundColor: palette.quality.overlayModal }}>
      <div
        className="relative flex flex-col rounded-xl w-[700px]"
        style={{
          backgroundColor: 'var(--color-card-bg)',
          border: '1px solid var(--color-border-subtle)',
          boxShadow: 'var(--shadow-dropdown)',
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4"
          style={{ borderBottom: `1px solid ${palette.borders.subtle}` }}>
          <div className="flex items-center gap-2">
            <FileText size={18} style={{ color: palette.accents.secondary.base }} />
            <h3 className="text-base font-semibold" style={{ color: palette.text.primary }}>Create Linear Issue</h3>
          </div>
          <button onClick={onClose} className="hover:opacity-70">
            <X size={18} style={{ color: palette.text.muted }} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Issue Template */}
          <div>
            <div className="text-sm font-medium mb-3" style={{ color: palette.text.primary }}>
              Linear Issue Template
            </div>
            <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--color-border-subtle)' }}>
              <div className="px-3 py-2 text-xs flex items-center justify-between"
                style={{ backgroundColor: 'var(--color-table-header-bg)', color: palette.text.muted }}>
                <span>Issue Description</span>
                <button onClick={handleCopyIssue} className="flex items-center gap-1 hover:opacity-80">
                  {issueCopied ? <Check size={14} /> : <Copy size={14} />}
                  <span className="text-[10px]">{issueCopied ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
              <div className="p-4 text-xs space-y-3" style={{ backgroundColor: 'var(--color-input-bg)', color: palette.text.primary }}>
                <div>
                  <strong>Business Task:</strong> {workflow?.name}
                </div>
                <div>
                  <strong>Optimization Opportunity:</strong>
                  <ul className="ml-4 mt-1">
                    <li>• Cost reduction: 68%</li>
                    <li>• Latency reduction: 39%</li>
                    <li>• Quality improvement: 5%</li>
                  </ul>
                </div>
                <div>
                  <strong>Recommended Changes:</strong><br />
                  Apply Lumoz simulation recommendations to optimize model selection and retrieval parameters.
                </div>
                <div>
                  <strong>Impact:</strong> Estimated savings of ${(workflow?.avg_cost_usd * workflow?.trace_count * 0.68).toFixed(2)} per month based on {workflow?.trace_count} traces.
                </div>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="text-xs" style={{ color: palette.text.muted }}>
            Copy the issue template above and paste into Linear. The issue will be tagged with business task optimization priority.
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4"
          style={{ borderTop: `1px solid ${palette.borders.subtle}` }}>
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs rounded-md transition-colors"
            style={{ color: palette.text.supporting, backgroundColor: 'var(--color-surface-hover)' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ResultMetricCard = ({ label, original, replay, delta, icon, invert }) => {
  const improved = invert ? delta > 0 : delta < 0;
  const color = delta === 0 ? palette.text.muted : improved ? palette.status.success : palette.status.danger;
  return (
    <div className="rounded-lg p-3 flex flex-col gap-2" style={{ border: `1px solid ${palette.borders.subtle}`, backgroundColor: `${color}06` }}>
      <div className="flex items-center gap-1.5" style={{ color: palette.text.muted }}>
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <div className="flex items-end gap-2">
        <span className="text-xs line-through" style={{ color: palette.text.muted }}>{original}</span>
        <span className="text-sm font-semibold" style={{ color: palette.text.primary }}>{replay}</span>
      </div>
      <div className="flex items-center gap-1 text-xs font-semibold" style={{ color }}>
        {delta < 0 ? <TrendingDown size={11} /> : delta > 0 ? <TrendingUp size={11} /> : <Minus size={11} />}
        {delta !== 0 ? `${deltaPrefix(delta)}${delta}%` : 'no change'}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// RecommendationDetailModal
// ─────────────────────────────────────────────────────────────────────────────
const RecommendationDetailModal = ({ rec, spans, onClose, onApply, applied }) => {
  if (!rec) return null;
  const span = spans.find(s => s.span_id === rec.span_id);
  const cfg = kindConfig(span?.span_kind || 'LLM');
  const typeColor = REC_COLOR[rec.type];

  // Determine what kind of change this is
  const hasPromptChange = rec.override.system_prompt !== undefined;
  const hasModelChange  = rec.override.model !== undefined;
  const hasParamChange  = rec.override.temperature !== undefined || rec.override.max_tokens !== undefined;
  const hasRetrieverChange = rec.override.top_k !== undefined || rec.override.similarity_threshold !== undefined;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: palette.quality.overlayModal }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="flex flex-col rounded-2xl overflow-hidden"
        style={{
          width: '80vw', maxWidth: 860, maxHeight: '80vh',
          backgroundColor: palette.neutrals.canvas,
          border: `1px solid ${palette.borders.medium}`,
          boxShadow: palette.quality.shadowModal,
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 shrink-0" style={{ borderBottom: `1px solid ${palette.borders.subtle}` }}>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <span style={{ color: typeColor, display: 'flex' }}>{REC_ICON[rec.type]}</span>
              <span className="text-sm font-semibold" style={{ color: palette.text.primary }}>{rec.action_label}</span>
              <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: withOpacity(cfg.color, 14), color: cfg.color, fontWeight: 600 }}>{rec.span_name}</span>
            </div>
            <p className="text-xs leading-relaxed" style={{ color: palette.text.muted, maxWidth: 560 }}>{rec.tooltip}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => { onApply(rec); onClose(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold"
              style={{
                backgroundColor: applied ? palette.neutrals.surfaceMid : typeColor,
                color: applied ? palette.text.muted : palette.text.inverse,
                cursor: 'pointer',
              }}
            >
              {applied ? <><Check size={11} /> Applied</> : <><Check size={11} /> Apply</>}
            </button>
            <button onClick={onClose} style={{ color: palette.text.muted }}><X size={14} /></button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5">
          {/* Impact summary */}
          <div className="flex gap-3">
            {rec.impact.cost !== 0 && (
              <div className="flex flex-col gap-1 px-4 py-3 rounded-lg flex-1" style={{ backgroundColor: palette.neutrals.surfaceSubtle, border: `1px solid ${palette.borders.subtle}` }}>
                <span className="text-xs" style={{ color: palette.text.muted }}>Est. Cost Impact</span>
                <span className="text-lg font-semibold" style={{ color: rec.impact.cost < 0 ? palette.status.success : palette.status.danger }}>
                  {deltaPrefix(rec.impact.cost)}{rec.impact.cost}%
                </span>
              </div>
            )}
            {rec.impact.latency !== 0 && (
              <div className="flex flex-col gap-1 px-4 py-3 rounded-lg flex-1" style={{ backgroundColor: palette.neutrals.surfaceSubtle, border: `1px solid ${palette.borders.subtle}` }}>
                <span className="text-xs" style={{ color: palette.text.muted }}>Est. Latency Impact</span>
                <span className="text-lg font-semibold" style={{ color: rec.impact.latency < 0 ? palette.status.success : palette.status.danger }}>
                  {deltaPrefix(rec.impact.latency)}{rec.impact.latency}%
                </span>
              </div>
            )}
            {rec.impact.quality !== 0 && (
              <div className="flex flex-col gap-1 px-4 py-3 rounded-lg flex-1" style={{ backgroundColor: palette.neutrals.surfaceSubtle, border: `1px solid ${palette.borders.subtle}` }}>
                <span className="text-xs" style={{ color: palette.text.muted }}>Est. Quality Impact</span>
                <span className="text-lg font-semibold" style={{ color: rec.impact.quality > 0 ? palette.status.success : palette.status.danger }}>
                  {deltaPrefix(rec.impact.quality)}{rec.impact.quality}%
                </span>
              </div>
            )}
            <div className="flex flex-col gap-1 px-4 py-3 rounded-lg" style={{ backgroundColor: palette.neutrals.surfaceSubtle, border: `1px solid ${palette.borders.subtle}` }}>
              <span className="text-xs" style={{ color: palette.text.muted }}>Confidence</span>
              <span className="text-sm font-medium capitalize" style={{ color: palette.text.primary }}>{rec.confidence}</span>
            </div>
          </div>

          {/* Model change */}
          {hasModelChange && (
            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: palette.text.muted }}>Model Change</span>
              <div className="flex items-center gap-3">
                <div className="flex-1 px-4 py-3 rounded-lg text-center" style={{ backgroundColor: withOpacity(palette.status.danger, 6), border: `1px solid ${withOpacity(palette.status.danger, 20)}` }}>
                  <span className="text-xs block mb-1" style={{ color: palette.text.muted }}>Current</span>
                  <span className="text-sm font-mono font-semibold" style={{ color: palette.text.primary }}>{span?.llm_model_name}</span>
                </div>
                <ChevronRight size={16} style={{ color: palette.text.muted, flexShrink: 0 }} />
                <div className="flex-1 px-4 py-3 rounded-lg text-center" style={{ backgroundColor: withOpacity(palette.status.success, 6), border: `1px solid ${withOpacity(palette.status.success, 20)}` }}>
                  <span className="text-xs block mb-1" style={{ color: palette.text.muted }}>Proposed</span>
                  <span className="text-sm font-mono font-semibold" style={{ color: palette.status.success }}>{rec.override.model}</span>
                </div>
              </div>
            </div>
          )}

          {/* Param changes */}
          {hasParamChange && (
            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: palette.text.muted }}>Parameter Changes</span>
              <div className="flex gap-3">
                {rec.override.temperature !== undefined && (
                  <div className="flex flex-col gap-1 px-4 py-3 rounded-lg" style={{ backgroundColor: palette.neutrals.surfaceSubtle, border: `1px solid ${palette.borders.subtle}` }}>
                    <span className="text-xs" style={{ color: palette.text.muted }}>Temperature</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono line-through" style={{ color: palette.text.muted }}>{span?.temperature ?? 1.0}</span>
                      <ChevronRight size={12} style={{ color: palette.text.muted }} />
                      <span className="text-sm font-mono font-semibold" style={{ color: palette.status.success }}>{rec.override.temperature}</span>
                    </div>
                  </div>
                )}
                {rec.override.max_tokens !== undefined && (
                  <div className="flex flex-col gap-1 px-4 py-3 rounded-lg" style={{ backgroundColor: palette.neutrals.surfaceSubtle, border: `1px solid ${palette.borders.subtle}` }}>
                    <span className="text-xs" style={{ color: palette.text.muted }}>Max Tokens</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono line-through" style={{ color: palette.text.muted }}>{span?.max_tokens ?? 'auto'}</span>
                      <ChevronRight size={12} style={{ color: palette.text.muted }} />
                      <span className="text-sm font-mono font-semibold" style={{ color: palette.status.success }}>{rec.override.max_tokens}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Retriever changes */}
          {hasRetrieverChange && (
            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: palette.text.muted }}>Retriever Config Changes</span>
              <div className="flex gap-3">
                {rec.override.top_k !== undefined && (
                  <div className="flex flex-col gap-1 px-4 py-3 rounded-lg" style={{ backgroundColor: palette.neutrals.surfaceSubtle, border: `1px solid ${palette.borders.subtle}` }}>
                    <span className="text-xs" style={{ color: palette.text.muted }}>top_k</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono line-through" style={{ color: palette.text.muted }}>{span?.retriever_config?.top_k}</span>
                      <ChevronRight size={12} style={{ color: palette.text.muted }} />
                      <span className="text-sm font-mono font-semibold" style={{ color: palette.status.success }}>{rec.override.top_k}</span>
                    </div>
                  </div>
                )}
                {rec.override.similarity_threshold !== undefined && (
                  <div className="flex flex-col gap-1 px-4 py-3 rounded-lg" style={{ backgroundColor: palette.neutrals.surfaceSubtle, border: `1px solid ${palette.borders.subtle}` }}>
                    <span className="text-xs" style={{ color: palette.text.muted }}>Similarity Threshold</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono line-through" style={{ color: palette.text.muted }}>{span?.retriever_config?.similarity_threshold}</span>
                      <ChevronRight size={12} style={{ color: palette.text.muted }} />
                      <span className="text-sm font-mono font-semibold" style={{ color: palette.status.success }}>{rec.override.similarity_threshold}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* System prompt diff */}
          {hasPromptChange && (
            <div className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: palette.text.muted }}>System Prompt</span>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs px-2 py-0.5 rounded self-start" style={{ backgroundColor: withOpacity(palette.status.danger, 10), color: palette.status.danger }}>Current</span>
                  <pre
                    className="text-xs leading-relaxed p-3 rounded-lg overflow-auto"
                    style={{
                      backgroundColor: withOpacity(palette.status.danger, 4),
                      border: `1px solid ${withOpacity(palette.status.danger, 20)}`,
                      color: palette.text.supporting,
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxHeight: 200,
                    }}
                  >
                    {span?.system_prompt || '(no system prompt)'}
                  </pre>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs px-2 py-0.5 rounded self-start" style={{ backgroundColor: withOpacity(palette.status.success, 10), color: palette.status.success }}>Proposed</span>
                  <pre
                    className="text-xs leading-relaxed p-3 rounded-lg overflow-auto"
                    style={{
                      backgroundColor: withOpacity(palette.status.success, 4),
                      border: `1px solid ${withOpacity(palette.status.success, 20)}`,
                      color: palette.text.supporting,
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxHeight: 200,
                    }}
                  >
                    {rec.override.system_prompt}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SimulationSpanList — compact 32px span list for the customize modal
// Inspired by Datadog/Jaeger: name + type dot + badge + duration, no waterfall bars
// ─────────────────────────────────────────────────────────────────────────────
const SimulationSpanList = ({ spans, selectedSpanId, onSelectSpan, activeOverrides }) => {
  const depths = useMemo(() => computeDepths(spans), [spans]);
  const [collapsed, setCollapsed] = useState(new Set());

  const hasChildren = useMemo(() => {
    const s = new Set();
    spans.forEach(sp => { if (sp.parent_span_id) s.add(sp.parent_span_id); });
    return s;
  }, [spans]);

  const visibleSpans = useMemo(() => {
    const hidden = new Set();
    return spans.filter(span => {
      if (hidden.has(span.span_id)) return false;
      if (collapsed.has(span.span_id)) {
        const q = [span.span_id];
        while (q.length) {
          const pid = q.shift();
          spans.forEach(s => { if (s.parent_span_id === pid) { hidden.add(s.span_id); q.push(s.span_id); } });
        }
      }
      return true;
    });
  }, [spans, collapsed]);

  return (
    <div className="flex flex-col">
      {/* Column headers */}
      <div
        className="flex items-center px-3 py-1.5 text-xs font-medium uppercase tracking-wider sticky top-0"
        style={{ color: palette.text.muted, borderBottom: `1px solid ${palette.borders.subtle}`, backgroundColor: palette.neutrals.canvas, letterSpacing: '0.06em', fontSize: 10 }}
      >
        <span style={{ flex: 1 }}>Span</span>
        <span style={{ width: 48, textAlign: 'right' }}>Tokens</span>
        <span style={{ width: 52, textAlign: 'right', paddingRight: 4 }}>Duration</span>
      </div>

      {visibleSpans.map(span => {
        const cfg = kindConfig(span.span_kind);
        const depth = depths[span.span_id] || 0;
        const isSelected = span.span_id === selectedSpanId;
        const hasOverride = activeOverrides.has(span.span_id);
        const hasKids = hasChildren.has(span.span_id);
        const tokens = (span.prompt_tokens || 0) + (span.completion_tokens || 0);
        const isCollapsed = collapsed.has(span.span_id);

        return (
          <div
            key={span.span_id}
            onClick={() => cfg.editable && onSelectSpan(span)}
            className="flex items-center group"
            style={{
              height: 32,
              paddingLeft: 12 + depth * 16,
              paddingRight: 8,
              cursor: cfg.editable ? 'pointer' : 'default',
              backgroundColor: isSelected
                ? withOpacity(palette.accents.secondary.base, 10)
                : 'transparent',
              borderLeft: isSelected
                ? `2px solid ${palette.accents.secondary.base}`
                : '2px solid transparent',
              borderBottom: `1px solid ${palette.borders.subtle}`,
              opacity: cfg.editable ? 1 : 0.55,
              transition: 'background-color 150ms',
            }}
            onMouseEnter={e => { if (!isSelected && cfg.editable) e.currentTarget.style.backgroundColor = palette.neutrals.surfaceSubtle; }}
            onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            {/* Collapse toggle */}
            <div style={{ width: 14, flexShrink: 0, marginRight: 2 }}>
              {hasKids && (
                <button
                  onClick={e => { e.stopPropagation(); setCollapsed(p => { const n = new Set(p); n.has(span.span_id) ? n.delete(span.span_id) : n.add(span.span_id); return n; }); }}
                  style={{ color: palette.text.muted, lineHeight: 1, display: 'flex' }}
                >
                  {isCollapsed ? <ChevronRight size={11} /> : <ChevronDown size={11} />}
                </button>
              )}
            </div>

            {/* Type color dot */}
            <div
              style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: cfg.color, flexShrink: 0, marginRight: 7 }}
            />

            {/* Span name */}
            <span
              className="text-xs truncate flex-1 min-w-0"
              style={{ color: isSelected ? palette.text.heading : palette.text.primary, fontWeight: isSelected ? 600 : 400 }}
            >
              {span.name}
            </span>

            {/* Type badge */}
            <span
              className="text-xs shrink-0 mx-2"
              style={{ color: cfg.color, fontSize: 10, fontWeight: 600, letterSpacing: '0.3px' }}
            >
              {cfg.label}
            </span>

            {/* Override dot */}
            {hasOverride && (
              <div style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: cfg.color, flexShrink: 0, marginRight: 4 }} title="Override applied" />
            )}

            {/* Tokens */}
            <span style={{ width: 48, textAlign: 'right', flexShrink: 0, color: palette.text.muted, fontSize: 11 }}>
              {tokens ? `${(tokens / 1000).toFixed(1)}k` : ''}
            </span>

            {/* Duration */}
            <span style={{ width: 52, textAlign: 'right', flexShrink: 0, color: palette.text.muted, fontSize: 11 }}>
              {fmtMs(span.duration_ms)}
            </span>

            {/* Pencil icon — editable rows, on hover */}
            {cfg.editable && (
              <Pencil
                size={10}
                style={{ color: isSelected ? palette.accents.secondary.base : palette.text.muted, opacity: isSelected ? 1 : 0, flexShrink: 0, marginLeft: 4 }}
                className="group-hover:opacity-100 transition-opacity"
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CustomizeModal — compact span list (left) + override editor (right)
// ─────────────────────────────────────────────────────────────────────────────
const CustomizeModal = ({ spans, activeOverrides, activeOverridesSet, onClose, onOverrideChange, onClearOverride }) => {
  const [selectedSpan, setSelectedSpan] = useState(null);
  const currentOverride = selectedSpan ? (activeOverrides[selectedSpan.span_id] || {}) : {};

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: palette.quality.overlayModal }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="flex flex-col rounded-2xl overflow-hidden"
        style={{
          width: '82vw', maxWidth: 960, height: '72vh',
          backgroundColor: palette.neutrals.canvas,
          border: `1px solid ${palette.borders.medium}`,
          boxShadow: palette.quality.shadowModal,
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-2.5 shrink-0"
          style={{ borderBottom: `1px solid ${palette.borders.subtle}` }}
        >
          <div className="flex items-center gap-2">
            <Pencil size={13} style={{ color: palette.accents.secondary.base }} />
            <span className="text-sm font-semibold" style={{ color: palette.text.primary }}>Customize Spans</span>
            <span className="text-xs" style={{ color: palette.text.muted }}>Select a span to configure its override</span>
          </div>
          <button
            onClick={onClose}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer ${ds.button.primary}`}
          >
            <Check size={10} strokeWidth={2.5} /> Done
          </button>
        </div>

        {/* Body — master / detail */}
        <div className="flex flex-1 min-h-0">
          {/* Span list — left */}
          <div className="overflow-y-auto" style={{ width: 340, flexShrink: 0, borderRight: `1px solid ${palette.borders.subtle}` }}>
            <SimulationSpanList
              spans={spans}
              selectedSpanId={selectedSpan?.span_id}
              onSelectSpan={setSelectedSpan}
              activeOverrides={activeOverridesSet}
            />
          </div>

          {/* Override editor — right */}
          <div className="flex-1 overflow-y-auto p-4">
            <OverrideEditorPanel
              span={selectedSpan}
              override={currentOverride}
              onChange={(ov) => { if (selectedSpan) onOverrideChange(selectedSpan.span_id, ov); }}
              onClear={() => { if (selectedSpan) onClearOverride(selectedSpan.span_id); }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main SimulationStudio
// ─────────────────────────────────────────────────────────────────────────────
const SimulationStudio = ({ appId, environment }) => {
  const [searchParams] = useSearchParams();
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [checkedRecIds, setCheckedRecIds] = useState(new Set());
  const [manualOverrides, setManualOverrides] = useState({});
  const [runStatus, setRunStatus] = useState('idle');
  const [showCustomize, setShowCustomize] = useState(false);
  const [viewedRec, setViewedRec] = useState(null);
  const [showClaudeCodeModal, setShowClaudeCodeModal] = useState(false);
  const [showLinearModal, setShowLinearModal] = useState(false);

  // Auto-select workflow from URL parameter (for navigation from signal cluster cards)
  useEffect(() => {
    const workflowParam = searchParams.get('workflow');
    if (workflowParam && !selectedWorkflow) {
      const workflowId = WORKFLOW_NAME_TO_ID[workflowParam];
      if (workflowId) {
        const workflow = MOCK_WORKFLOWS.find(wf => wf.id === workflowId);
        if (workflow) {
          setSelectedWorkflow(workflow);
        }
      }
    }
  }, [searchParams, selectedWorkflow]);

  const recommendations = selectedWorkflow ? (MOCK_RECOMMENDATIONS[selectedWorkflow.id] || []) : [];
  const spans = MOCK_RAG_SPANS; // In real impl: fetched by workflow_id

  // Merge rec overrides + manual overrides (manual wins)
  const activeOverrides = useMemo(() => {
    const merged = {};
    checkedRecIds.forEach(recId => {
      const rec = recommendations.find(r => r.id === recId);
      if (rec) merged[rec.span_id] = { ...(merged[rec.span_id] || {}), ...rec.override };
    });
    Object.entries(manualOverrides).forEach(([spanId, ov]) => {
      merged[spanId] = { ...(merged[spanId] || {}), ...ov };
    });
    return merged;
  }, [checkedRecIds, manualOverrides, recommendations]);

  const activeOverridesSet = useMemo(() => new Set(Object.keys(activeOverrides)), [activeOverrides]);
  const overrideCount = activeOverridesSet.size;

  const handleToggleRec = useCallback((rec) => {
    setCheckedRecIds(prev => {
      const next = new Set(prev);
      next.has(rec.id) ? next.delete(rec.id) : next.add(rec.id);
      return next;
    });
  }, []);

  const handleOverrideChange = useCallback((spanId, newOverride) => {
    setManualOverrides(prev => ({ ...prev, [spanId]: newOverride }));
  }, []);

  const handleClearOverride = useCallback((spanId) => {
    setManualOverrides(prev => { const n = { ...prev }; delete n[spanId]; return n; });
    setCheckedRecIds(prev => {
      const next = new Set(prev);
      recommendations.filter(r => r.span_id === spanId).forEach(r => next.delete(r.id));
      return next;
    });
  }, [recommendations]);

  const handleRun = useCallback(() => {
    setRunStatus('running');
    setTimeout(() => setRunStatus('done'), 2000);
  }, []);

  const handleBack = useCallback(() => {
    setSelectedWorkflow(null);
    setCheckedRecIds(new Set());
    setManualOverrides({});
    setRunStatus('idle');
    setShowCustomize(false);
  }, []);

  // ── Workflow list ──
  if (!selectedWorkflow) {
    return <WorkflowList onSelect={setSelectedWorkflow} />;
  }

  return (
    <div className="flex flex-col gap-4 p-6" style={{ minHeight: 0 }}>
      {/* Breadcrumb */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleBack}
          className="flex items-center gap-1.5 text-sm"
          style={{ color: palette.text.muted }}
          onMouseEnter={e => e.currentTarget.style.color = palette.text.primary}
          onMouseLeave={e => e.currentTarget.style.color = palette.text.muted}
        >
          <ArrowLeft size={14} /> Business Tasks
        </button>
        <span style={{ color: palette.borders.strong }}>/</span>
        <span className="text-sm font-semibold" style={{ color: palette.text.primary }}>{selectedWorkflow.name}</span>
        <span className="text-xs" style={{ color: palette.text.muted }}>· {selectedWorkflow.trace_count} traces · representative trace loaded</span>
      </div>

      {/* Recommendations + run */}
      <RecommendationPanel
        recommendations={recommendations}
        checkedIds={checkedRecIds}
        onToggle={handleToggleRec}
        manualOverrides={manualOverrides}
        spans={spans}
        onClearManual={handleClearOverride}
        onCustomize={() => setShowCustomize(true)}
        onView={setViewedRec}
        onRun={handleRun}
        runStatus={runStatus}
      />

      {/* Recommendation detail modal */}
      {viewedRec && (
        <RecommendationDetailModal
          rec={viewedRec}
          spans={spans}
          applied={checkedRecIds.has(viewedRec.id)}
          onApply={handleToggleRec}
          onClose={() => setViewedRec(null)}
        />
      )}

      {/* Customize modal */}
      {showCustomize && (
        <CustomizeModal
          spans={spans}
          activeOverrides={activeOverrides}
          activeOverridesSet={activeOverridesSet}
          onClose={() => setShowCustomize(false)}
          onOverrideChange={handleOverrideChange}
          onClearOverride={handleClearOverride}
        />
      )}

      {/* Results — always visible */}
      <ResultsPanel
        workflow={selectedWorkflow}
        hasResults={runStatus === 'done'}
        runStatus={runStatus}
        setShowClaudeCodeModal={setShowClaudeCodeModal}
        setShowLinearModal={setShowLinearModal}
      />

      {/* Claude Code Modal */}
      {showClaudeCodeModal && (
        <ClaudeCodeModal
          workflow={selectedWorkflow}
          onClose={() => setShowClaudeCodeModal(false)}
        />
      )}

      {/* Linear Issue Modal */}
      {showLinearModal && (
        <LinearIssueModal
          workflow={selectedWorkflow}
          onClose={() => setShowLinearModal(false)}
        />
      )}

    </div>
  );
};

export default SimulationStudio;
